import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiPlusLight, PiMinusLight } from 'react-icons/pi';

/**
 * THE spreadsheet surface - read AND write, in the cells (files-18).
 *
 * Editing a sheet used to mean leaving it: an "Edit CSV" toggle swapped the
 * grid for a textarea of comma-separated text, which is a different document
 * from the one the reader was looking at. The grid itself is now the editor —
 * click a cell and type, Tab and Enter move the way they do in a spreadsheet,
 * and rows and columns can be added or removed at the end.
 *
 * It renders NO chrome of its own: the sheet tabs, the save button and the
 * scroll container belong to `ExcelEditor`, so this stays one thing.
 */

export type SheetRow = any[];

interface SheetGridProps {
  rows: SheetRow[];
  /** Read-only when omitted. */
  onChange?: (rows: SheetRow[]) => void;
  /** First row is the header band. */
  showHeaders?: boolean;
  /** Reading zoom factor, 1 = 100%. */
  zoom?: number;
}

const columnCount = (rows: SheetRow[]): number =>
  rows.reduce((max, row) => Math.max(max, Array.isArray(row) ? row.length : 1), 0);

/** A rectangular copy - sheet_to_json yields ragged rows, cells need a home. */
const normalise = (rows: SheetRow[], cols: number): SheetRow[] =>
  rows.map((row) => {
    const source = Array.isArray(row) ? row : [row];
    const next = source.slice(0, cols);
    while (next.length < cols) next.push('');
    return next;
  });

const SheetGrid: React.FC<SheetGridProps> = ({ rows, onChange, showHeaders = true, zoom = 1 }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<{ row: number; col: number } | null>(null);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const cols = Math.max(columnCount(rows), 1);
  const grid = useMemo(() => normalise(rows, cols), [rows, cols]);
  const editable = Boolean(onChange);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = useCallback(
    (value: string, cell: { row: number; col: number }) => {
      if (!onChange) return;
      const next = grid.map((row) => [...row]);
      while (next.length <= cell.row) next.push(new Array(cols).fill(''));
      next[cell.row][cell.col] = value;
      onChange(next);
    },
    [grid, cols, onChange]
  );

  const openCell = useCallback(
    (row: number, col: number) => {
      if (!editable) return;
      setEditing({ row, col });
      const value = grid[row]?.[col];
      setDraft(value === null || value === undefined ? '' : String(value));
    },
    [editable, grid]
  );

  const closeCell = useCallback(
    (moveTo?: { row: number; col: number }) => {
      if (editing) commit(draft, editing);
      if (moveTo && moveTo.row < grid.length && moveTo.col < cols) {
        setEditing(moveTo);
        const value = grid[moveTo.row]?.[moveTo.col];
        setDraft(value === null || value === undefined ? '' : String(value));
      } else {
        setEditing(null);
        setDraft('');
      }
    },
    [editing, draft, commit, grid, cols]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!editing) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        closeCell({ row: editing.row + 1, col: editing.col });
      } else if (e.key === 'Tab') {
        e.preventDefault();
        const forward = !e.shiftKey;
        const col = editing.col + (forward ? 1 : -1);
        if (col >= 0 && col < cols) {
          closeCell({ row: editing.row, col });
        } else if (forward) {
          closeCell({ row: editing.row + 1, col: 0 });
        } else {
          closeCell({ row: editing.row - 1, col: cols - 1 });
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setEditing(null);
        setDraft('');
      }
    },
    [editing, closeCell, cols]
  );

  const addRow = () => onChange?.([...grid.map((r) => [...r]), new Array(cols).fill('')]);
  const removeRow = () => {
    if (grid.length <= 1) return;
    onChange?.(grid.slice(0, -1).map((r) => [...r]));
  };
  const addColumn = () => onChange?.(grid.map((row) => [...row, '']));
  const removeColumn = () => {
    if (cols <= 1) return;
    onChange?.(grid.map((row) => row.slice(0, -1)));
  };

  if (grid.length === 0) {
    return (
      <div className="flex items-center justify-center w-full py-12">
        <p className="text-fg-muted text-sm">{t('No data to display')}</p>
      </div>
    );
  }

  const headerRow = showHeaders ? grid[0] : null;
  const bodyStart = showHeaders ? 1 : 0;

  const cellContent = (rowIdx: number, colIdx: number, value: any, isHeader: boolean) => {
    const isEditing = editing?.row === rowIdx && editing?.col === colIdx;
    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => closeCell()}
          onKeyDown={handleKeyDown}
          className="w-full px-3 py-1.5 bg-surface text-fg-strong border-none outline-none ring-1 ring-champagne rounded-none text-sm"
          aria-label={t('Cell value')}
        />
      );
    }
    return (
      <div
        className={`px-3 py-1.5 truncate ${isHeader ? 'font-semibold text-fg-strong' : 'text-fg-body'}`}
        title={value === null || value === undefined ? '' : String(value)}
      >
        {value}
      </div>
    );
  };

  return (
    <div className="w-full" style={{ zoom }}>
      <table className="border-collapse text-sm w-max min-w-full">
        {headerRow && (
          <thead>
            <tr className="bg-surface-sunken/70 border-b border-hairline">
              {headerRow.map((cell, colIdx) => (
                <th
                  key={colIdx}
                  className={`border-e border-hairline-soft text-start whitespace-nowrap min-w-[120px] ${
                    editable ? 'cursor-text' : ''
                  }`}
                  onClick={() => openCell(0, colIdx)}
                >
                  {cellContent(0, colIdx, cell, true)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {grid.slice(bodyStart).map((row, idx) => {
            const rowIdx = idx + bodyStart;
            return (
              <tr
                key={rowIdx}
                className={`border-b border-hairline-soft ${idx % 2 === 0 ? '' : 'bg-surface-soft/50'}`}
              >
                {row.map((cell, colIdx) => (
                  <td
                    key={colIdx}
                    className={`border-e border-hairline-soft whitespace-nowrap min-w-[120px] transition-colors ${
                      editable ? 'cursor-text hover:bg-surface-sunken/50' : ''
                    }`}
                    onClick={() => openCell(rowIdx, colIdx)}
                  >
                    {cellContent(rowIdx, colIdx, cell, false)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {editable && (
        <div className="flex items-center gap-4 px-3 py-2">
          <div className="flex items-center gap-1 text-[11px] text-fg-muted">
            <span>{t('Rows')}</span>
            <button type="button" onClick={addRow} title={t('Add row')} className="p-1 rounded hover:bg-surface-sunken/70 hover:text-champagne">
              <PiPlusLight className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={removeRow} title={t('Remove last row')} className="p-1 rounded hover:bg-surface-sunken/70 hover:text-champagne">
              <PiMinusLight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex items-center gap-1 text-[11px] text-fg-muted">
            <span>{t('Columns')}</span>
            <button type="button" onClick={addColumn} title={t('Add column')} className="p-1 rounded hover:bg-surface-sunken/70 hover:text-champagne">
              <PiPlusLight className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={removeColumn} title={t('Remove last column')} className="p-1 rounded hover:bg-surface-sunken/70 hover:text-champagne">
              <PiMinusLight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SheetGrid;
